import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Truck, Phone, MapPin, Check, X, PackageCheck, Clock, Package, Banknote, Wallet, Navigation, CalendarClock, FileDown, Flag, LogOut } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/orderStatus";

const cardBorder = (done, failed) => (done ? "border-[#2D6A4F]/40 opacity-60" : failed ? "border-[#D00000]/40 opacity-60" : "border-black/5");

const FAIL_REASONS = ["Client absent", "Adresse introuvable", "Client injoignable", "Autre"];

export default function DriverTour() {
  const { token } = useParams();
  const qc = useQueryClient();
  const [confirmDeliver, setConfirmDeliver] = useState(null); // order to deliver
  const [failFor, setFailFor] = useState(null); // order to fail
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [completing, setCompleting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["tour", token],
    queryFn: async () => (await api.get(`/delivery/tour/${token}`)).data,
    refetchInterval: 20000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["tour", token] });

  const deliver = async () => {
    setBusy(true);
    try {
      await api.post(`/delivery/tour/${token}/orders/${confirmDeliver.order_id}`, { result: "delivered" });
      toast.success("Livraison confirmée ✅ Le marchand est notifié.");
      setConfirmDeliver(null); refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitFail = async () => {
    if (!reason) { toast.error("Sélectionnez un motif d'échec."); return; }
    setBusy(true);
    try {
      await api.post(`/delivery/tour/${token}/orders/${failFor.order_id}`, { result: "failed", reason, comment });
      toast.success("Échec enregistré. Le marchand est notifié.");
      setFailFor(null); setReason(""); setComment(""); refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const continueTour = () => {
    toast.success("Progression enregistrée 👍 Vous pouvez fermer et revenir autant de fois que nécessaire via ce lien (valable 7 jours). La tournée reste active tant que vous ne l'avez pas terminée.");
  };

  const tryFinish = () => {
    if (!data?.all_processed) { toast.error("Il reste des clients à traiter dans votre tournée."); return; }
    setConfirmComplete(true);
  };

  const finishTour = async () => {
    setCompleting(true);
    try {
      const { data: res } = await api.post(`/delivery/tour/${token}/complete`);
      toast.success("Tournée terminée. Rapport envoyé au marchand 🧾");
      setConfirmComplete(false); refresh();
      if (res.report_url) window.open(res.report_url, "_blank");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setCompleting(false); }
  };

  if (isLoading) return <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center text-[#9c968a]">Chargement…</div>;

  if (data?.expired && !data?.completed) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex flex-col items-center justify-center px-6 text-center" data-testid="tour-expired">
        <Clock className="h-14 w-14 text-[#D00000] mb-3" />
        <h1 className="font-display text-xl font-extrabold mb-1">Lien expiré</h1>
        <p className="text-sm text-[#5C574F]">Ce lien de tournée a expiré (validité 7 jours). Demandez-en un nouveau au marchand.</p>
      </div>
    );
  }

  const orders = data?.orders || [];
  const completed = !!data?.completed;
  const cur = orders[0]?.currency || "XOF";
  const pending = orders.filter((o) => o.status !== "livre" && o.status !== "echec_livraison");
  const codOrders = orders.filter((o) => (o.to_collect ?? o.total) > 0);
  const totalItems = codOrders.reduce((s, o) => s + (o.items_amount || 0), 0);
  const totalFees = codOrders.reduce((s, o) => s + (o.delivery_fee || 0), 0);
  const grandTotal = orders.reduce((s, o) => s + (o.to_collect ?? o.total ?? 0), 0);

  const stops = pending.map((o) => [o.quartier, o.city].filter(Boolean).join(", ")).filter(Boolean);
  let routeUrl = null;
  if (stops.length) {
    const dest = encodeURIComponent(stops[stops.length - 1]);
    const wps = stops.slice(0, -1).map(encodeURIComponent).join("|");
    routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving` + (wps ? `&waypoints=${wps}` : "");
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0] pb-28">
      <div className="bg-[#1B4332] text-white px-5 pt-8 pb-5">
        <div className="flex items-center gap-2 mb-1"><Truck className="h-6 w-6" /><h1 className="font-display text-xl font-extrabold">Feuille de route</h1></div>
        <p className="text-white/80 text-sm">{data?.business_name} • {orders.length} client(s) • {pending.length} à livrer</p>
      </div>

      {/* Synthèse globale */}
      <div className="px-4 -mt-3">
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 grid grid-cols-3 gap-2" data-testid="tour-summary">
          <div className="text-center">
            <Package className="h-4 w-4 text-[#0077B6] mx-auto mb-1" />
            <p className="text-[10px] text-[#9c968a] leading-tight">Articles à encaisser</p>
            <p className="font-display font-bold text-sm text-[#1A1A1A]" data-testid="summary-items">{money(totalItems, cur)}</p>
          </div>
          <div className="text-center border-x border-[#E5E0D8]">
            <Banknote className="h-4 w-4 text-[#E85D04] mx-auto mb-1" />
            <p className="text-[10px] text-[#9c968a] leading-tight">Frais de livraison</p>
            <p className="font-display font-bold text-sm text-[#1A1A1A]" data-testid="summary-fees">{money(totalFees, cur)}</p>
          </div>
          <div className="text-center">
            <Wallet className="h-4 w-4 text-[#2D6A4F] mx-auto mb-1" />
            <p className="text-[10px] text-[#9c968a] leading-tight">Total à encaisser</p>
            <p className="font-display font-bold text-sm text-[#2D6A4F]" data-testid="summary-grand-total">{money(grandTotal, cur)}</p>
          </div>
        </div>
        {routeUrl && !completed && (
          <a href={routeUrl} target="_blank" rel="noreferrer" data-testid="route-btn" className="mt-3 w-full h-12 rounded-2xl bg-[#0077B6] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Navigation className="h-5 w-5" /> Itinéraire ({stops.length} arrêt{stops.length > 1 ? "s" : ""})
          </a>
        )}
      </div>

      {completed && (
        <div className="px-4 mt-3">
          <div className="bg-[#2D6A4F] text-white rounded-2xl p-4 text-center" data-testid="tour-completed-banner">
            <PackageCheck className="h-7 w-7 mx-auto mb-1" />
            <p className="font-display font-bold">Tournée terminée</p>
            <p className="text-white/80 text-sm mb-3">Ce lien est désormais verrouillé.</p>
            {data?.report_url && (
              <a href={data.report_url} target="_blank" rel="noreferrer" data-testid="download-report-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white text-[#2D6A4F] font-bold">
                <FileDown className="h-5 w-5" /> Télécharger le rapport (PDF)
              </a>
            )}
          </div>
        </div>
      )}

      {/* Liste des clients (boutons en ligne) */}
      <div className="px-4 mt-3 space-y-3">
        {orders.map((o, i) => {
          const done = o.status === "livre";
          const failed = o.status === "echec_livraison";
          return (
            <motion.div
              key={o.order_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              data-testid={`tour-order-${o.order_id}`}
              className={`bg-white rounded-2xl border shadow-sm p-4 ${cardBorder(done, failed)}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className={`font-display font-bold text-lg leading-tight ${done || failed ? "line-through text-[#9c968a]" : ""}`}>{o.customer_name}</p>
                  <a href={`tel:${o.customer_phone}`} data-testid={`call-${o.order_id}`} className="text-sm text-[#0077B6] flex items-center gap-1 mt-0.5"><Phone className="h-3.5 w-3.5" /> {o.customer_phone}</a>
                </div>
                <span className="text-xs text-[#9c968a]">{o.invoice_number || `#${o.order_id.slice(-6)}`}</span>
              </div>
              <p className="text-sm text-[#5C574F] flex items-center gap-1.5 mb-2"><MapPin className="h-4 w-4 text-[#E85D04]" /> {[o.quartier, o.city].filter(Boolean).join(", ") || "Adresse non précisée"}</p>
              {o.slots?.length > 0 && (
                <p className="text-xs text-[#5C574F] flex items-start gap-1.5 mb-2 bg-[#FFF4E6] rounded-lg px-2 py-1.5" data-testid={`slots-${o.order_id}`}>
                  <CalendarClock className="h-3.5 w-3.5 text-[#E85D04] mt-0.5 shrink-0" />
                  <span><span className="font-semibold">Créneaux :</span> {o.slots.map((s) => `${s.date} (${s.time})`).join(" ou ")}</span>
                </p>
              )}

              <div className="bg-[#F9F6F0] rounded-xl p-3 space-y-1 text-sm mb-3">
                <div className="flex justify-between"><span className="text-[#5C574F]">{o.n_items} article(s)</span><span className="font-semibold">{money(o.items_amount, o.currency)}</span></div>
                {(o.articles || []).length > 0 && (
                  <div className="border-t border-[#E5E0D8] pt-1 space-y-0.5" data-testid={`articles-${o.order_id}`}>
                    {o.articles.map((a, ai) => (
                      <div key={ai} className="text-xs text-[#5C574F] flex justify-between">
                        <span>{a.qty}× {a.name}</span>
                        {a.size ? <span className="font-bold text-[#1B4332]">Taille {a.size}</span> : null}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between"><span className="text-[#5C574F]">Frais de livraison</span><span className="font-semibold">{money(o.delivery_fee, o.currency)}</span></div>
                {o.paid ? (
                  <div className="flex justify-between items-center border-t border-[#E5E0D8] pt-1 font-display font-bold" data-testid={`paid-online-${o.order_id}`}>
                    <span className="text-[#5C574F]">Total ({money(o.total, o.currency)})</span>
                    <span className="text-[#2D6A4F] flex items-center gap-1"><Check className="h-4 w-4" /> Payé en ligne</span>
                  </div>
                ) : (
                  <div className="flex justify-between border-t border-[#E5E0D8] pt-1 font-display font-bold text-[#1A1A1A]" data-testid={`to-collect-${o.order_id}`}><span>À encaisser (espèces)</span><span className="text-[#E85D04]">{money(o.total, o.currency)}</span></div>
                )}
              </div>

              {done ? (
                <div className="flex items-center justify-center gap-2 h-12 rounded-xl bg-[#2D6A4F]/10 text-[#2D6A4F] font-bold" data-testid={`delivered-tag-${o.order_id}`}><PackageCheck className="h-5 w-5" /> Livré</div>
              ) : failed ? (
                <div className="rounded-xl bg-[#D00000]/8 border border-[#D00000]/20 p-3 text-center" data-testid={`failed-tag-${o.order_id}`}>
                  <p className="text-[#D00000] font-bold text-sm">Échec de livraison</p>
                  {o.delivery_failed_reason && <p className="text-xs text-[#5C574F] mt-0.5">{o.delivery_failed_reason}</p>}
                </div>
              ) : completed ? null : (
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid={`deliver-btn-${o.order_id}`} onClick={() => setConfirmDeliver(o)} className="h-12 rounded-xl bg-[#2D6A4F] text-white font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform"><Check className="h-5 w-5" /> Livré</button>
                  <button data-testid={`fail-btn-${o.order_id}`} onClick={() => { setFailFor(o); setReason(""); setComment(""); }} className="h-12 rounded-xl font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform bg-white border border-[#D00000]/40 text-[#D00000]"><X className="h-5 w-5" /> Échec</button>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="text-center text-xs text-[#9c968a] mt-6">Lien sécurisé (7 jours) • Propulsé par SuccèsNet</p>

      {/* Barre permanente : Continuer + Terminer */}
      {!completed && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E5E0D8] px-4 py-3 grid grid-cols-2 gap-2" data-testid="tour-footer">
          <button onClick={continueTour} data-testid="continue-tour-btn" className="h-13 py-3.5 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform border border-[#E5E0D8]">
            <LogOut className="h-5 w-5" /> Quitter (reprendre plus tard)
          </button>
          <button
            onClick={tryFinish}
            disabled={completing}
            data-testid="finish-tour-btn"
            className={`h-13 py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-colors ${data?.all_processed ? "bg-[#1B4332] text-white active:scale-95" : "bg-[#E5E0D8] text-[#9c968a]"}`}
          >
            <Flag className="h-5 w-5" /> {completing ? "Clôture…" : "Terminer la tournée"}
          </button>
        </div>
      )}

      {/* Confirmation Livré */}
      <AnimatePresence>
        {confirmDeliver && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setConfirmDeliver(null)} data-testid="confirm-deliver-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 text-center">
              <div className="h-12 w-12 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] flex items-center justify-center mx-auto mb-3"><Check className="h-6 w-6" /></div>
              <h3 className="font-display text-lg font-extrabold mb-1">Êtes-vous sûr ?</h3>
              {confirmDeliver.paid ? (
                <p className="text-sm text-[#5C574F] mb-4">Marquer la commande de <span className="font-semibold">{confirmDeliver.customer_name}</span> comme <span className="font-semibold">livrée</span>. <span className="font-semibold text-[#2D6A4F]">Déjà payée en ligne — rien à encaisser.</span></p>
              ) : (
                <p className="text-sm text-[#5C574F] mb-4">Marquer la commande de <span className="font-semibold">{confirmDeliver.customer_name}</span> comme <span className="font-semibold">livrée</span> et encaisser <span className="font-semibold text-[#E85D04]">{money(confirmDeliver.total, confirmDeliver.currency)}</span> en espèces.</p>
              )}
              <div className="flex gap-2">
                <button data-testid="confirm-deliver-cancel" onClick={() => setConfirmDeliver(null)} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">Annuler</button>
                <button data-testid="confirm-deliver-ok" onClick={deliver} disabled={busy} className="flex-1 h-12 rounded-2xl bg-[#2D6A4F] text-white font-bold disabled:opacity-60">{busy ? "…" : "Oui, livré"}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirmation Échec (avec motif) */}
      <AnimatePresence>
        {failFor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setFailFor(null)} data-testid="fail-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
              <h3 className="font-display text-lg font-extrabold mb-1">Échec de livraison</h3>
              <p className="text-sm text-[#5C574F] mb-3">{failFor.customer_name} — sélectionnez un motif.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {FAIL_REASONS.map((r) => (
                  <button key={r} data-testid={`fail-reason-${r}`} onClick={() => setReason(r)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${reason === r ? "bg-[#D00000] text-white border-[#D00000]" : "bg-[#F9F6F0] text-[#5C574F] border-transparent"}`}>{r}</button>
                ))}
              </div>
              <textarea data-testid="fail-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (facultatif)…" rows={2} className="w-full bg-[#F9F6F0] rounded-xl px-3 py-2 outline-none border border-transparent focus:border-[#D00000] mb-4 resize-none text-sm" />
              <div className="flex gap-2">
                <button data-testid="fail-cancel-btn" onClick={() => setFailFor(null)} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">Annuler</button>
                <button data-testid="fail-confirm-btn" onClick={submitFail} disabled={busy} className="flex-1 h-12 rounded-2xl bg-[#D00000] text-white font-bold disabled:opacity-60">{busy ? "…" : "Confirmer l'échec"}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirmation clôture */}
      <AnimatePresence>
        {confirmComplete && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setConfirmComplete(false)} data-testid="confirm-complete-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
              <h3 className="font-display text-lg font-extrabold mb-1">Terminer la tournée ?</h3>
              <p className="text-sm text-[#5C574F] mb-4">Une fois terminée, le lien deviendra <span className="font-semibold">inactif</span> et un rapport PDF sera envoyé au marchand. Ne terminez que lorsque toutes les livraisons sont traitées.</p>
              <div className="flex gap-2">
                <button data-testid="complete-cancel-btn" onClick={() => setConfirmComplete(false)} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">Annuler</button>
                <button data-testid="complete-confirm-btn" onClick={finishTour} disabled={completing} className="flex-1 h-12 rounded-2xl bg-[#1B4332] text-white font-bold disabled:opacity-60">{completing ? "…" : "Terminer"}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
